import { ApiV1_Catalog, ApiV1_Category, ApiV1_Product } from './api-v1.types';

/**
 * Qué hacer con las categorías dinámicas al recorrer el catálogo.
 *
 * Una categoría dinámica no contiene productos propios: agrupa por tag productos que YA viven en su
 * categoría real. Por eso recorrerlas repite productos, y por eso el modo importa.
 *
 * - `excluded` — se saltean. Cada producto aparece UNA sola vez.
 * - `included` — se recorren. Un producto etiquetado aparece repetido, conservando su `categoryId`.
 * - `included-with-attribution` — se recorren y se reescribe `categoryId` con la categoría por la
 *   que se llegó al producto. Es lo que permite filtrar por una categoría dinámica: el producto
 *   aparece una vez por cada categoría que lo contiene, atribuido a cada una.
 */
export type ApiV1_DynamicCategoriesMode = 'excluded' | 'included' | 'included-with-attribution';

/**
 * `active` conserva sólo lo publicado (productos activos, sin sus presentaciones ocultas, y sin los
 * productos que quedan sin ninguna presentación visible); `all` no filtra.
 *
 * Son DOS ejes separados y no uno de "visibilidad" porque hay usos legítimos que los combinan: el
 * selector de productos del order-editor necesita categorías activas pero con TODOS sus productos,
 * porque además de listarlas resuelve por id un producto que ya está en el carrito y que el comercio
 * puede haber ocultado después.
 */
export type ApiV1_CatalogFilter = 'all' | 'active';

export type ApiV1_CatalogProductsOptions = {
  dynamicCategories: ApiV1_DynamicCategoriesMode;
  categories: ApiV1_CatalogFilter;
  products: ApiV1_CatalogFilter;
};

export type ApiV1_CatalogCategoriesOptions = {
  dynamicCategories: Exclude<ApiV1_DynamicCategoriesMode, 'included-with-attribution'>;
  categories: ApiV1_CatalogFilter;
  products: ApiV1_CatalogFilter;
  /**
   * Qué hacer con las categorías que quedan sin productos que mostrar. Se evalúa DESPUÉS de filtrar
   * productos, así que una categoría cuyos productos están todos ocultos cuenta como vacía.
   *
   * - `kept` — se conservan. Lo necesita el editor de catálogo del admin: una categoría recién
   *   creada todavía no tiene productos y el comercio tiene que poder verla para llenarla.
   * - `dropped` — se descartan. Es lo que evita renderizarle al comprador una sección vacía.
   */
  emptyCategories: 'kept' | 'dropped';
};

const selectCategories = (
  catalog: Pick<ApiV1_Catalog, 'categories'> | null | undefined,
  options: { dynamicCategories: ApiV1_DynamicCategoriesMode; categories: ApiV1_CatalogFilter },
): ApiV1_Category[] =>
  (catalog?.categories ?? [])
    .filter(category => options.dynamicCategories !== 'excluded' || !category.isDynamic)
    .filter(category => options.categories === 'all' || category.active);

export const withVisiblePresentations = (product: ApiV1_Product): ApiV1_Product | null => {
  if (!product.presentations) {
    return product;
  }
  const visibleItems = product.presentations.items.filter(item => !item.hidden);

  if (visibleItems.length === product.presentations.items.length) {
    return product;
  }
  if (visibleItems.length === 0) {
    return null;
  }
  return { ...product, presentations: { ...product.presentations, items: visibleItems } };
};

const selectCategoryProducts = (
  category: ApiV1_Category,
  products: ApiV1_CatalogFilter,
): ApiV1_Product[] =>
  products === 'active'
    ? (category.products ?? [])
        .filter(product => product.active)
        .map(withVisiblePresentations)
        .filter((product): product is ApiV1_Product => product !== null)
    : [...(category.products ?? [])];

/**
 * Categorías del catálogo, con sus productos ya filtrados según `products`.
 *
 * Sobre las copias, con precisión: el array devuelto, los objetos categoría y sus arrays `products`
 * son NUEVOS, así que ordenar o agregar sobre el resultado no toca el catálogo. Los objetos
 * PRODUCTO, en cambio, se comparten — mutar uno sí se ve desde el catálogo y desde los demás
 * consumidores. No se clonan a propósito: serían miles de objetos por evaluación, en tres apps, y
 * hoy nadie los muta (el único punto del workspace que lo hace, `cart-item-utils`, clona antes).
 */
export function getCatalogCategories(
  catalog: Pick<ApiV1_Catalog, 'categories'> | null | undefined,
  options: ApiV1_CatalogCategoriesOptions,
): ApiV1_Category[] {
  return selectCategories(catalog, options)
    .map(category => ({
      ...category,
      products: selectCategoryProducts(category, options.products),
    }))
    .filter(category => options.emptyCategories === 'kept' || category.products.length > 0);
}

/**
 * Productos del catálogo, aplanados en ORDEN DE CATÁLOGO: categorías por `sortOrder` y, dentro de
 * cada una, productos por `sortOrder`, tal como los devuelve el endpoint. `ApiV1_Product` no expone
 * `sortOrder`, así que ese orden vive en la posición del array.
 *
 * No lleva `emptyCategories`: una categoría sin productos no aporta productos.
 */
export function getCatalogProducts(
  catalog: Pick<ApiV1_Catalog, 'categories'> | null | undefined,
  options: ApiV1_CatalogProductsOptions,
): ApiV1_Product[] {
  return selectCategories(catalog, options).flatMap(category => {
    const products = selectCategoryProducts(category, options.products);

    return options.dynamicCategories === 'included-with-attribution'
      ? products.map(product => ({ ...product, categoryId: category.id }))
      : products;
  });
}

export type ApiV1_FindActiveProductOptions = {
  productIdOrSlug: string;
  categoryIdOrSlug?: string;
};

const matchesIdOrSlug =
  (idOrSlug: string) =>
  (item: { id: string; slug?: string }): boolean =>
    item.id === idOrSlug || item.slug === idOrSlug;

export function findActiveCatalogProduct(
  catalog: Pick<ApiV1_Catalog, 'categories'> | null | undefined,
  options: ApiV1_FindActiveProductOptions,
): ApiV1_Product | undefined {
  const matchesProduct = matchesIdOrSlug(options.productIdOrSlug);
  const categories = selectCategories(catalog, {
    dynamicCategories: options.categoryIdOrSlug ? 'included' : 'excluded',
    categories: 'active',
  });
  const candidateCategories = options.categoryIdOrSlug
    ? categories.filter(matchesIdOrSlug(options.categoryIdOrSlug))
    : categories;

  for (const category of candidateCategories) {
    const product = category.products?.find(matchesProduct);

    if (product?.active) {
      return withVisiblePresentations(product) ?? undefined;
    }
  }
  return undefined;
}
